import React from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import dayjs from "dayjs"
import colors from "../../Constants/colors"

import PYPLayoutContainer from "../PYPLayoutContainer/PYPLayoutContainer.js"
import PYPPartTitleContent from "../PYPPartTitleContent/PYPPartTitleContent.js"
import RoadmapperDraggableContainer from "../RoadmapperDraggableContainer/RoadmapperDraggableContainer"

import Actions from "../../Redux/Actions"

const moment = dayjs
/*  Component Functionality
  -Pull in data for part (ideal personal/work day)
  -Aggregate data into array of time events + constraints
  -Aggregate top 10 values
*/

const styles = {
  container: {
    alignItems: "center",
    textAlign: "center"
  },
  layoutContainer: {
    justifyContent: "start"
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch)
}

const mapStateToProps = (state, props) => {
  const currentPart = state.pyp?.parts?.byId?.[state.pyp?.navigation?.currentPartId]
  const partDataId = currentPart?.partData

  let metadata = { title: null, description: null }
  let timeEvents = null
  let eventOrder = null
  let constraints = []
  let buttonValues = []

  // The partData will get fetched separately
  const partData =
    partDataId &&
    state.pyp?.partData?.byId?.[partDataId] &&
    state.pyp.partData.byId[partDataId].data

  if (partData) {
    // Try partTitle first (for some part types), then fall back to title
    if (partData.partTitle && Array.isArray(partData.partTitle) && partData.partTitle.length > 0) {
      metadata = partData.partTitle[0]
    } else if (partData.title && Array.isArray(partData.title) && partData.title.length > 0) {
      metadata = partData.title[0]
    }
    
    // eventOrder can be an object with sequence property, or directly the sequence array
    if (partData.eventOrder) {
      if (Array.isArray(partData.eventOrder)) {
        // Already an array
        eventOrder = partData.eventOrder
      } else if (typeof partData.eventOrder === 'object' && partData.eventOrder.sequence !== undefined) {
        // Object with sequence property
        const sequence = partData.eventOrder.sequence
        if (Array.isArray(sequence)) {
          eventOrder = sequence
        } else if (typeof sequence === 'string') {
          // Convert comma-separated string to array
          eventOrder = sequence.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
        } else {
          eventOrder = null
        }
      } else {
        eventOrder = null
      }
    }
    
    // Safely access constraints
    if (partData.referencedPartData?.constraints && Array.isArray(partData.referencedPartData.constraints) && partData.referencedPartData.constraints.length >= 2) {
      const constraint0Text = partData.referencedPartData.constraints[0]?.text
      const constraint1Text = partData.referencedPartData.constraints[1]?.text
      
      // Try multiple formats to parse the time
      let startMoment = null
      let endMoment = null
      
      if (constraint0Text) {
        // Try different formats
        startMoment = moment(constraint0Text, "hh:mm a", true)
        if (!startMoment.isValid()) {
          startMoment = moment(constraint0Text, "h:mm a", true)
        }
        if (!startMoment.isValid()) {
          startMoment = moment(constraint0Text, "HH:mm", true)
        }
        if (!startMoment.isValid()) {
          startMoment = moment(constraint0Text, "H:mm", true)
        }
      }
      
      if (constraint1Text) {
        // Try different formats
        endMoment = moment(constraint1Text, "hh:mm a", true)
        if (!endMoment.isValid()) {
          endMoment = moment(constraint1Text, "h:mm a", true)
        }
        if (!endMoment.isValid()) {
          endMoment = moment(constraint1Text, "HH:mm", true)
        }
        if (!endMoment.isValid()) {
          endMoment = moment(constraint1Text, "H:mm", true)
        }
      }
      
      // Only set constraints if both are valid
      if (startMoment && startMoment.isValid() && endMoment && endMoment.isValid()) {
        constraints = {
          start: startMoment,
          end: endMoment,
          startId: partData.referencedPartData.constraints[0].id,
          endId: partData.referencedPartData.constraints[1].id
        }
      }
    }
    
    // Safely access buttonValues
    if (partData.referencedPartData?.buttonValues && Array.isArray(partData.referencedPartData.buttonValues)) {
      // Filter to show only the 10 values selected in Stage 1 (partId=20)
      // A value is selected in Stage 1 if it has ANY buttonSelections (length > 0)
      // This is used for both partId=77 and partId=78
      buttonValues = partData.referencedPartData.buttonValues.filter(
        (buttonValue) => {
          if (!buttonValue || !buttonValue.buttonSelections || !Array.isArray(buttonValue.buttonSelections)) {
            return false
          }
          
          // Show values that have at least one selection (the 10 selected in Stage 1, partId=20)
          // These selections can have stage: null, 1, or 2 - all represent selections from Stage 1
          return buttonValue.buttonSelections.length > 0
        }
      )
      
      // Add "Non-Value" option at the beginning
      buttonValues.unshift({
        id: null,
        name: "Non-Value"
      })
    }
    
    // Safely access timeEvents
    if (partData.userTimeEvents && Array.isArray(partData.userTimeEvents)) {
      timeEvents = partData.userTimeEvents.map((event) => {
        // Try to get value name from buttonValues array
        let valueName = "Non-Value"
        if (event.value_id !== null && event.value_id !== undefined) {
          // First try to find in buttonValues array (the 10 selected values)
          const matchingButton = buttonValues.find((button) => button && button.id === event.value_id)
          if (matchingButton) {
            valueName = matchingButton.name || "Non-Value"
          } else if (event.value) {
            // Fallback: use value from API if available (from preload or previous mapping)
            if (typeof event.value === 'object' && event.value.name) {
              valueName = event.value.name
            } else if (typeof event.value === 'string' && event.value !== "Non-Value") {
              valueName = event.value
            }
          }
        } else if (event.value) {
          // If no value_id but we have a value property, use it
          if (typeof event.value === 'object' && event.value.name) {
            valueName = event.value.name
          } else if (typeof event.value === 'string') {
            valueName = event.value
          }
        }
        
        return {
          ...event,
          value: valueName,
          value_id: event.value_id !== undefined ? event.value_id : null // Ensure value_id is preserved
        }
      })
    }
  }

  return {
    ...props,
    pyp: state.pyp,
    currentPart,
    partDataId,
    metadata,
    initializedPart: state.pyp.initializedPart,
    fetchingPartData: state.pyp.fetchingPartData,
    initializingPYPInputs: state.pyp.initializingPYPInputs,
    timeEvents,
    eventOrder,
    constraints,
    buttonValues
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  class LayoutMetaDataReferenceUserTimeEvents extends React.Component {
    constructor(props) {
      super(props)
      this.state = {
        timeEvents: null,
        loading: true
      }
    }

    componentDidMount() {
      // Track when loading started
      this.loadingStartTime = Date.now()
      
      // Always set a timeout to ensure loading stops even if fetchPartData hangs
      const loadingTimeout = setTimeout(() => {
        if (this.state.loading) {
          this.setState({ loading: false })
        }
      }, 3000) // 3 second timeout
      
      // Also check if data is already available (e.g., from previous navigation)
      if (this.props.partDataId && this.props.pyp.partData?.byId?.[this.props.partDataId]) {
        clearTimeout(loadingTimeout)
        this.setState({ loading: false })
        return
      }
      
      // If already fetching, wait for it to complete
      if (this.props.pyp.fetchingPartData) {
        // Will be handled in componentDidUpdate when fetchingPartData becomes false
        return
      }
      
      if (!this.props.pyp.savingPYP) {
        this.props.fetchPartData().then(() => {
          clearTimeout(loadingTimeout)
          this.setState({ loading: false })
        }).catch((error) => {
          clearTimeout(loadingTimeout)
          // Even if fetch fails, stop loading to show the page
          this.setState({ loading: false })
        })
      } else {
        // If saving, still set loading to false after a short delay
        clearTimeout(loadingTimeout)
        setTimeout(() => {
          this.setState({ loading: false })
        }, 100)
      }
    }

    componentDidUpdate(prevProps) {
      // If partData becomes available, stop loading immediately
      if (this.state.loading && this.props.partDataId && this.props.pyp.partData?.byId?.[this.props.partDataId]) {
        this.setState({ loading: false })
      }
      
      // If fetchingPartData changes from true to false, stop loading
      if (prevProps.pyp.fetchingPartData && !this.props.pyp.fetchingPartData && this.state.loading) {
        this.setState({ loading: false })
      }
      
      // If currentPartId changes, we're on a new part, so stop loading
      if (prevProps.pyp?.navigation?.currentPartId !== this.props.pyp?.navigation?.currentPartId && this.state.loading) {
        this.setState({ loading: false })
      }
      
      // If we've been loading for more than 3 seconds, force stop
      if (this.state.loading && this.loadingStartTime && Date.now() - this.loadingStartTime > 3000) {
        this.setState({ loading: false })
      }
      
      if (prevProps.pyp.savingPYP && !this.props.pyp.savingPYP) {
        this.props.fetchPartData().then(() => {
          this.setState({ loading: false })
        }).catch((error) => {
          // Even if fetch fails, stop loading to show the page
          this.setState({ loading: false })
        })
      }
    }

    render() {
      return (
        <PYPLayoutContainer
          backgroundColor={colors.background}
          style={{
            ...styles.layoutContainer
          }}
        >
          <PYPPartTitleContent
            title={this.props.metadata.title}
            description={this.props.metadata.description}
          />
          <div style={styles.container} id="DraggableContainer">
            <RoadmapperDraggableContainer
              values={this.props.buttonValues}
              momentFormat={this.props.momentFormat || ["h:mm a", "h:mm a"]}
              momentJoiner={this.props.momentJoiner || " - "}
              timeConstraints={
                (this.props.constraints && this.props.constraints.start && this.props.constraints.end) 
                  ? this.props.constraints 
                  : {
                      start: moment("6:00", "hh:mm a"),
                      end: moment("20:00", "hh:mm")
                    }
              }
              loading={this.state.loading}
              durationUnit={this.props.durationUnit || "minutes"}
              userTimeEvents={this.props.timeEvents}
              eventOrder={this.props.eventOrder}
            />
          </div>
        </PYPLayoutContainer>
      )
    }
  }
)
